<!--
  Базовый селект с поиском, очисткой и дополнительными действиями

  Основные props:
  - options: массив { value, label } для отображения в списке
  - modelValue: id выбранной записи
  - externalSearch: включает поиск через wrapper/store вместо локальной фильтрации
  - withButton: показывает кнопку создания новой записи в dropdown
  - editable: показывает кнопку редактирования выбранной записи рядом с очисткой
  - editTitle: tooltip и aria-label для кнопки редактирования
  - selectedAsCard: заменяет trigger выбранной записи содержимым slot selected-card

  BaseSelect не знает, что именно редактируется. Он только эмитит edit-option с
  текущим modelValue. Доменный wrapper открывает нужную модалку, сохраняет
  запись, обновляет свой store/options и вызывает reselectSelectedOption.

  Полный контракт поведения и обязательные проверки:
  ../../../_docs/contracts/base_select_contract.md

  Пример wrapper-а:

  <BaseSelect
    ref="selectRef"
    :options="formattedOptions"
    :model-value="modelValue"
    editable
    @edit-option="openEditModal"
    @change="$emit('change', $event.value)"
  />

  В режиме selectedAsCard wrapper передаёт доменную карточку через scoped slot:

  <BaseSelect selected-as-card>
    <template #selected-card="{ selectedOption, clearSelection }">
      <EntityLink
        :entity="selectedOption.entity"
        relation-card
        @remove="clearSelection"
      />
    </template>
  </BaseSelect>

  handleSaved(record) {
    store.replaceRecord(record);
    this.$nextTick(() => {
      this.$refs.selectRef?.reselectSelectedOption();
    });
  }
-->
<template>
  <div
    v-bind="$attrs"
    class="custom-select"
    :class="{
      'is-open': isOpen,
      'has-selection': modelValue,
      'is-disabled': disabled,
      'has-custom-dropdown': customDropdown,
      'has-inline-search': inlineSearch,
      'has-confirmed-selection': inlineSearch && selectedOption,
    }"
  >
    <!-- Триггер выбора -->
    <slot
      v-if="showsSelectedCard"
      name="selected-card"
      :selected-option="selectedOption"
      :clear-selection="clearSelection"
    />

    <div
      v-else
      class="select-trigger"
      :class="{ 'is-open': isOpen, invalid: invalid }"
      @click="handleTriggerClick"
      @wheel="handleInlineSearchWheel"
      @touchstart.passive="handleInlineSearchTouchStart"
      @touchmove="handleInlineSearchTouchMove"
      @blur="onBlur"
      :tabindex="disabled ? -1 : 0"
      :aria-disabled="disabled"
    >
      <span class="selected-value">
        <template v-if="$slots['selected-value']">
          <slot
            name="selected-value"
            :selected-option="selectedOption"
            :placeholder="placeholder"
          />
        </template>
        <!-- Компонент значения селектора -->
        <base-select-value
          v-else
          ref="selectValue"
          :selected-option="selectedOption"
          :inline-search="inlineSearch"
          :inline-search-is-active="inlineSearchIsActive"
          :inline-search-loading="inlineSearchLoading"
          :search-query="searchQuery"
          :inline-search-placeholder="inlineSearchPlaceholder"
          :placeholder="placeholder"
          :disabled="disabled"
          @click="openDropdown"
          @focus="openDropdown"
          @input="handleInlineSearchInput"
          @mousedown="handleInlineSearchMouseDown"
          @keydown="handleInlineSearchKeydown"
        />
      </span>
      <!-- Кнопки управления селектором -->
      <base-select-controls
        :has-selection="Boolean(modelValue)"
        :editable="editable"
        :clearable="clearable"
        :disabled="disabled"
        :edit-title="editTitle"
        :is-open="isOpen"
        @edit="editSelectedOption"
        @clear="clearSelection"
      />
    </div>

    <!-- Выпадающий список -->
    <template v-if="!showsSelectedCard">
      <Teleport to="body">
        <div
        v-show="isOpen"
        ref="dropdown"
        class="select-dropdown"
        :class="{
          'select-dropdown--custom': customDropdown,
        }"
        :style="dropdownStyles"
        @click.stop
        @mousedown.stop
      >
          <!-- Пользовательское содержимое dropdown из wrapper-а -->
          <template v-if="customDropdown">
            <slot
              name="dropdown-content"
              :selected-option="selectedOption"
              :search-query="searchQuery"
              :set-search-query="setCustomSearchQuery"
              :select-option="selectOption"
              :close-dropdown="closeDropdown"
            />
          </template>

          <!-- Стандартное содержимое BaseSelect -->
          <template v-else>
            <!-- Поиск внутри открытого dropdown -->
            <div class="search-container" v-if="searchable && !inlineSearch">
              <div class="search-input-wrapper">
                <svg
                  class="search-icon"
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="currentColor"
                >
                  <path
                    d="M11.5 11.5L14 14M7 12a5 5 0 100-10 5 5 0 000 10z"
                    stroke="currentColor"
                    stroke-width="2"
                    fill="none"
                  />
                </svg>
                <input
                  ref="searchInput"
                  v-model="searchQuery"
                  type="text"
                  class="search-input"
                  :placeholder="searchPlaceholder"
                  @input="handleSearchInput"
                  @keydown.enter="!autoSearch && handleSearch()"
                  @keydown="handleSearchKeydown"
                />
                <!-- Очистка введённого поискового запроса -->
                <button
                  v-if="searchQuery"
                  class="clear-search"
                  @click.stop="clearSearch"
                  type="button"
                >
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 12 12"
                    fill="currentColor"
                  >
                    <path
                      d="M1 1l10 10M11 1L1 11"
                      stroke="currentColor"
                      stroke-width="2"
                    />
                  </svg>
                </button>
              </div>
              <button
                v-if="!autoSearch"
                class="search-button"
                @click="handleSearch"
                :disabled="!searchQuery.trim()"
                type="button"
              >
                Найти
              </button>
            </div>

            <!-- Прокручиваемая область списка -->
            <div
              class="dropdown-content"
              ref="dropdownContent"
              @scroll="handleDropdownScroll"
            >
              <!-- Пустое состояние списка или поиска -->
              <div
                v-if="displayedOptions.length === 0"
                class="no-results"
              >
                {{ emptyStateText }}
              </div>

              <!-- Список доступных опций -->
              <div
                v-for="option in displayedOptions"
                :key="option.value"
                class="select-option"
                :class="{
                  'is-selected': isOptionSelected(option),
                  'is-disabled': option.disabled,
                  'is-highlighted': isOptionHighlighted(option),
                }"
                @mousedown="handleOptionMouseDown($event)"
                @click="selectOption(option)"
                @mouseenter="setHighlightedOption(option)"
              >
                <span class="option-label">
                  <span
                    v-if="option.color"
                    class="select-color-dot"
                    :style="{ backgroundColor: option.color }"
                  ></span>
                  <span
                    v-html="highlightSearchText(option.label)"
                  ></span>
                </span>
                <span
                  v-if="isOptionSelected(option)"
                  class="checkmark"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="currentColor"
                  >
                    <path
                      d="M13.5 4.5L6 12L2.5 8.5"
                      stroke="currentColor"
                      stroke-width="2"
                      fill="none"
                    />
                  </svg>
                </span>
              </div>
            </div>

            <!-- Дополнительное действие внизу списка -->
            <div
              v-if="withButton"
              class="select-button"
              @click="handleButtonClick"
            >
              <span class="button-icon">+</span>
              <span class="button-label">{{ buttonLabel }}</span>
            </div>

            <!-- Счётчик найденных опций после поиска -->
            <div
              v-if="
                searchable &&
                searchPerformed &&
                displayedOptions.length > 0
              "
              class="search-info"
            >
              Найдено: {{ displayedOptions.length }}
              <template v-if="!externalSearch">
                из {{ options.length }}
              </template>
            </div>
          </template>
        </div>
      </Teleport>
    </template>
  </div>
</template>

<script>
import {
  getBaseSelectDropdownPosition,
  getBaseSelectEmptyStateText,
  getBaseSelectResizeObserverTargets,
  getBaseSelectSearchQuery,
  getBaseSelectSpinnerHideDelay,
  shouldEmitBaseSelectSearch,
  shouldDebounceBaseSelectSearch,
} from "@/utils/baseSelectUtils";

export default {
  name: "BaseSelect",
  inheritAttrs: false,

  props: {
    options: {
      type: Array,
      default: () => [],
      validator: (value) => {
        return value.every(
          (option) =>
            option &&
            typeof option.value !== "undefined" &&
            option.label
        );
      },
    },

    modelValue: {
      type: [String, Number, Boolean, Object],
      default: null,
    },

    placeholder: {
      type: String,
      default: "Выберите опцию",
    },

    // Настройки поиска
    searchable: {
      type: Boolean,
      default: false,
    },

    // Режим, в котором поиск вводится прямо в trigger селекта
    inlineSearch: {
      type: Boolean,
      default: false,
    },

    // Wrapper передает true только после фактического старта HTTP-запроса.
    // Debounce и простой ввод не должны запускать spinner: он сообщает именно
    // об ожидании ответа сервера, а не о наборе текста.
    loading: {
      type: Boolean,
      default: false,
    },

    searchPlaceholder: {
      type: String,
      default: "Поиск...",
    },

    noResultsText: {
      type: String,
      default: "Ничего не найдено",
    },

    // Если true — поиск срабатывает автоматически с задержкой, без кнопки "Найти"
    autoSearch: {
      type: Boolean,
      default: true,
    },

    // Задержка debounce в мс (актуально только при autoSearch: true)
    autoSearchDelay: {
      type: Number,
      default: 400,
    },

    // Возможность очистки выбора
    clearable: {
      type: Boolean,
      default: true,
    },

    // Показывает кнопку редактирования выбранной записи рядом с clear/dropdown
    editable: {
      type: Boolean,
      default: false,
    },

    // Текст tooltip/aria-label для кнопки редактирования
    editTitle: {
      type: String,
      default: "Редактировать",
    },

    // Настройки кнопки
    withButton: {
      type: Boolean,
      default: false,
    },

    buttonLabel: {
      type: String,
      default: "Добавить новую опцию",
    },

    // Если true — фильтрация на стороне сервера, компонент только отображает
    externalSearch: {
      type: Boolean,
      default: false,
    },

    invalid: {
      type: Boolean,
      default: false,
    },

    disabled: {
      type: Boolean,
      default: false,
    },

    customDropdown: {
      type: Boolean,
      default: false,
    },

    // Wrapper может заменить выбранное значение доменной карточкой через
    // slot selected-card. По умолчанию BaseSelect сохраняет обычный trigger.
    selectedAsCard: {
      type: Boolean,
      default: false,
    },

  },

  emits: [
    "update:modelValue",
    "button-click",
    "change",
    "search",
    "clear-search",
    "dropdown-scroll-end",
    "edit-option",
  ],

  data() {
    // Состояние поиска и позиционирования хранится здесь, потому что dropdown
    // принадлежит BaseSelect и открывается рядом со своим trigger.
    return {
      isOpen: false,
      searchQuery: "",
      highlightedOption: null,
      filteredOptions: [],
      searchPerformed: false,
      dropdownStyles: {},
      debounceTimer: null,
      lastExternalSearchQuery: null,
      dropdownPositionTimers: [],
      dropdownLayoutResizeObserver: null,
      inlineSearchTouchY: null,
      inlineSearchLoading: false,
      inlineSearchLoadingStartedAt: null,
      inlineSearchLoadingHideTimer: null,
    };
  },

  computed: {
    showsSelectedCard() {
      return Boolean(
        this.selectedAsCard &&
        this.modelValue !== null &&
        typeof this.modelValue !== "undefined" &&
        this.modelValue !== "" &&
        this.$slots["selected-card"]
      );
    },

    // Найдем выбранную опцию
    selectedOption() {
      return (
        this.options.find(
          (option) => String(option.value) == String(this.modelValue)
        ) || null
      );
    },

    // Для локального поиска используем его результат из filteredOptions.
    // Внешний поиск получает новый список асинхронно через prop options: читать
    // его напрямую важно для inline-поля на мобильном, иначе dropdown может
    // остаться на копии списка, сделанной до прихода ответа сервера.
    displayedOptions() {
      return this.externalSearch ? this.options : this.filteredOptions;
    },

    emptyStateText() {
      return getBaseSelectEmptyStateText({
        optionsCount: this.options.length,
        searchPerformed: this.searchPerformed,
        noResultsText: this.noResultsText,
        inlineSearch: this.inlineSearch,
        autoSearch: this.autoSearch,
      });
    },

    // В inline-режиме input одновременно показывает выбранный label и запрос.
    // Иконка и placeholder зависят от того, находится ли он в режиме поиска.
    inlineSearchIsActive() {
      if (!this.inlineSearch || !this.searchable) return false;

      return (
        this.isOpen ||
        (!this.selectedOption && Boolean(this.searchQuery.trim()))
      );
    },

    inlineSearchPlaceholder() {
      return this.inlineSearchIsActive
        ? this.searchPlaceholder
        : this.placeholder;
    },
  },

  watch: {
    // Стор завершает HTTP-запрос в том же реактивном обновлении, в котором
    // передает новые options. Откладываем выключение индикатора на следующий
    // render tick, чтобы на медленном мобильном устройстве он не исчезал до
    // появления новых строк dropdown-а.
    loading: {
      handler(isLoading) {
        if (isLoading) {
          clearTimeout(this.inlineSearchLoadingHideTimer);
          this.inlineSearchLoadingHideTimer = null;
          if (!this.inlineSearchLoading) {
            this.inlineSearchLoadingStartedAt = Date.now();
          }
          this.inlineSearchLoading = true;
          return;
        }

        this.$nextTick(() => {
          if (this.loading || !this.inlineSearchLoading) return;

          const delay = getBaseSelectSpinnerHideDelay({
            startedAt: this.inlineSearchLoadingStartedAt,
            currentTime: Date.now(),
          });
          this.inlineSearchLoadingHideTimer = setTimeout(() => {
            if (!this.loading) {
              this.inlineSearchLoading = false;
              this.inlineSearchLoadingStartedAt = null;
            }
          }, delay);
        });
      },
      immediate: true,
    },

    modelValue: {
      handler() {
        // Внешний v-model меняется после выбора, очистки или обновления записи
        // в модалке. Синхронизируем label, не ожидая ввода пользователя.
        this.syncInlineSearchQuery();
      },
      immediate: true,
    },

    // Реактивно обновляем отфильтрованные опции при изменении исходного массива
    options: {
      handler() {
        if (this.externalSearch || !this.searchPerformed) {
          this.filteredOptions = [...this.options];
        }
        this.syncInlineSearchQuery();
      },
      deep: true,
      immediate: true,
    },

    // Пока dropdown открыт, отслеживаем размер и видимую область окна: они
    // определяют, открывать список сверху или снизу. VisualViewport меняется
    // при появлении экранной клавиатуры iPad без обычного window resize.
    isOpen(newVal) {
      if (newVal) {
        this.handleDropdownOpen();
        return;
      }

      this.handleDropdownClose();
    },
  },

  methods: {
    handleDropdownOpen() {
      // Сразу ставим dropdown над или под текущим trigger.
      this.updateDropdownPosition();

      // Даём выбирать option стрелками и Enter, пока список открыт.
      document.addEventListener("keydown", this.handleKeydown);

      // Закрываем список, когда пользователь кликнул за пределами селекта.
      document.addEventListener("click", this.handleClickOutside);

      // Держим dropdown у trigger при прокрутке страницы или контейнера.
      window.addEventListener("scroll", this.updateDropdownPosition, true);

      // Пересчитываем сторону списка при изменении размера окна.
      window.addEventListener("resize", this.updateDropdownPosition);

      // На iPad клавиатура меняет visual viewport без обычного window resize.
      this.addDropdownViewportListeners();

      // Модалка или форма могут изменить размер и сдвинуть trigger без resize окна.
      this.addDropdownLayoutResizeObserver();

      // iPad показывает клавиатуру после focus, поэтому повторяем расчёт позже.
      this.scheduleDropdownPositionUpdates();
      this.$nextTick(() => {
        if (
          !this.customDropdown &&
          this.searchable &&
          this.inlineSearch &&
            this.$refs.selectValue
          ) {
            this.$refs.selectValue.focusInput();
        } else if (
          !this.customDropdown &&
          this.searchable &&
          this.$refs.searchInput
        ) {
          this.$refs.searchInput.focus();
        }
        this.scheduleDropdownPositionUpdates();
      });
    },

    // При закрытии освобождаем все глобальные обработчики и отложенные
    // пересчёты, иначе закрытый селект продолжит реагировать на scroll.
    handleDropdownClose() {
      document.removeEventListener("keydown", this.handleKeydown);
      document.removeEventListener("click", this.handleClickOutside);
      window.removeEventListener("scroll", this.updateDropdownPosition, true);
      window.removeEventListener("resize", this.updateDropdownPosition);
      this.removeDropdownViewportListeners();
      this.removeDropdownLayoutResizeObserver();
      this.clearDropdownPositionTimers();
      this.inlineSearchTouchY = null;
      clearTimeout(this.debounceTimer);
      if (!this.inlineSearch) {
        this.searchQuery = "";
      } else {
        // Inline-search временно очищает текст выбранной записи при открытии,
        // чтобы сразу показать полный список. Если пользователь закрыл селект
        // без нового ввода или выбора, возвращаем label текущего modelValue.
        this.syncInlineSearchQuery();
      }
      this.searchPerformed = false;
      this.lastExternalSearchQuery = null;
      if (!this.externalSearch) {
        this.filteredOptions = [...this.options];
      }
      if (this.externalSearch) {
        this.$emit("clear-search");
      }
    },

    // visualViewport отражает реально видимую область при zoom и клавиатуре.
    // Fallback сохраняет обычное desktop-поведение в браузерах без этого API.
    getViewportBounds() {
      const viewport = window.visualViewport;

      if (!viewport) {
        return {
          left: 0,
          top: 0,
          width: window.innerWidth,
          height: window.innerHeight,
          right: window.innerWidth,
          bottom: window.innerHeight,
        };
      }

      return {
        left: viewport.offsetLeft || 0,
        top: viewport.offsetTop || 0,
        width: viewport.width,
        height: viewport.height,
        right: (viewport.offsetLeft || 0) + viewport.width,
        bottom: (viewport.offsetTop || 0) + viewport.height,
      };
    },

    // Обновляет позицию dropdown-а относительно trigger в viewport.
    updateDropdownPosition() {
      const trigger = this.$el?.querySelector(".select-trigger");
      if (!trigger) return;

      const rect = trigger.getBoundingClientRect();
      const viewport = this.getViewportBounds();
      const dropdownPosition = getBaseSelectDropdownPosition({
        triggerRect: {
          // DOMRect отсчитывается от layout viewport. Границы visualViewport
          // уже содержат offsetTop/offsetLeft при сдвиге под клавиатуру, поэтому
          // нельзя прибавлять этот offset к rect второй раз.
          top: rect.top,
          bottom: rect.bottom,
          left: rect.left,
          width: rect.width,
        },
        viewport,
        inlineSearch: this.inlineSearch,
      });

      const horizontalGap = 12;
      const maxWidth = Math.max(0, viewport.width - horizontalGap * 2);
      const width = Math.min(rect.width, maxWidth);
      const left = Math.min(
        Math.max(rect.left, viewport.left + horizontalGap),
        viewport.right - horizontalGap - width
      );

      this.dropdownStyles = {
        position: "fixed",
        left: `${left}px`,
        width: `${width}px`,
        minWidth: this.customDropdown ? "auto" : "min(240px, 100%)",
        maxHeight: `${dropdownPosition.maxHeight}px`,
        "--base-select-dropdown-content-max-height": `${dropdownPosition.maxHeight}px`,
        zIndex: 2002,
        ...(dropdownPosition.openUpward
          ? {
              top: "auto",
              bottom: `${window.innerHeight - rect.top + dropdownPosition.triggerGap}px`,
            }
          : {
              top: `${rect.bottom + dropdownPosition.triggerGap}px`,
              bottom: "auto",
            }),
      };
    },

    // Изменения visual viewport не всегда сопровождаются window resize,
    // поэтому слушаем оба события только пока dropdown открыт.
    addDropdownViewportListeners() {
      window.visualViewport?.addEventListener(
        "resize",
        this.updateDropdownPosition
      );
      window.visualViewport?.addEventListener(
        "scroll",
        this.updateDropdownPosition
      );
    },

    // Парный cleanup для addDropdownViewportListeners.
    removeDropdownViewportListeners() {
      window.visualViewport?.removeEventListener(
        "resize",
        this.updateDropdownPosition
      );
      window.visualViewport?.removeEventListener(
        "scroll",
        this.updateDropdownPosition
      );
    },

    // window.resize не срабатывает, если сузилась только модалка или другой
    // родитель. Наблюдаем select и его layout-контейнер, пока список открыт,
    // чтобы absolute dropdown не остался у прежнего положения trigger.
    addDropdownLayoutResizeObserver() {
      if (!window.ResizeObserver || this.dropdownLayoutResizeObserver) {
        return;
      }

      this.dropdownLayoutResizeObserver = new window.ResizeObserver(() => {
        if (this.isOpen) this.updateDropdownPosition();
      });
      getBaseSelectResizeObserverTargets(this.$el).forEach((element) => {
        this.dropdownLayoutResizeObserver.observe(element);
      });
    },

    // Observer существует только у открытого dropdown, иначе каждый BaseSelect
    // продолжал бы следить за layout страницы после закрытия.
    removeDropdownLayoutResizeObserver() {
      this.dropdownLayoutResizeObserver?.disconnect();
      this.dropdownLayoutResizeObserver = null;
    },

    // После focus iPad сначала открывает клавиатуру, затем меняет viewport.
    // Несколько коротких пересчётов ловят оба состояния без постоянного polling.
    scheduleDropdownPositionUpdates() {
      this.clearDropdownPositionTimers();
      [0, 120, 280, 520].forEach((delay) => {
        const timer = window.setTimeout(() => {
          if (this.isOpen) this.updateDropdownPosition();
        }, delay);
        this.dropdownPositionTimers.push(timer);
      });
    },

    // Не даём таймерам от ранее открытого dropdown изменить следующий.
    clearDropdownPositionTimers() {
      this.dropdownPositionTimers.forEach((timer) => {
        window.clearTimeout(timer);
      });
      this.dropdownPositionTimers = [];
    },

    // Dropdown находится внутри this.$el, поэтому клик за пределами корня
    // означает, что пользователь закончил работу с селектом.
    handleClickOutside(event) {
      if (!this.isOpen) return;

      const trigger = this.$el;
      const dropdown = this.$refs.dropdown;

      if (
        trigger &&
        !trigger.contains(event.target) &&
        dropdown &&
        !dropdown.contains(event.target)
      ) {
        this.isOpen = false;
      }
    },

    handleTriggerClick() {
      if (this.inlineSearch) {
        // На iPad focus по input приходит раньше click по trigger. Если здесь
        // использовать toggle, селект успевает открыться и сразу закрыться:
        // клавиатура остается, а список и иконка поиска пропадают.
        this.openDropdown();
        return;
      }

      this.toggleDropdown();
    },

    // Переключение dropdown
    toggleDropdown() {
      if (this.disabled) return;

      if (this.isOpen) {
        this.closeDropdown();
        return;
      }

      this.openDropdown();
    },

    openDropdown() {
      if (this.disabled) return;
      const wasOpen = this.isOpen;

      this.isOpen = true;
      if (this.inlineSearch && !wasOpen) this.prepareInlineSearchDropdown();
    },

    // При открытии inline-search выбранный label не должен становиться запросом:
    // пользователь ожидает увидеть полный список и начать новый поиск.
    prepareInlineSearchDropdown() {
      if (this.selectedOption && this.searchQuery === this.selectedOption.label) {
        this.searchQuery = "";
      }
      this.searchPerformed = false;
      this.filteredOptions = [...this.options];
      this.highlightedOption = null;
      this.ensureInlineDropdownSpace();
    },

    // Inline-search всегда открывается вниз, чтобы input оставался у клавиатуры.
    // Если места мало, прокручиваем документ прежде, чем считать позицию списка.
    ensureInlineDropdownSpace() {
      this.$nextTick(() => {
        const trigger = this.$el?.querySelector(".select-trigger");
        if (!trigger) return;

        const rect = trigger.getBoundingClientRect();
        const viewport = this.getViewportBounds();
        // Используем ту же систему координат, что и updateDropdownPosition.
        // На iOS visualViewport смещается при клавиатуре, а DOMRect нет.
        const spaceBelow = viewport.bottom - rect.bottom;
        const minimumVisibleListHeight = 160;

        if (spaceBelow < minimumVisibleListHeight) {
          const scrollOffset =
            minimumVisibleListHeight - spaceBelow + 12;
          window.scrollBy({
            top: scrollOffset,
            behavior: "smooth",
          });
          this.scheduleDropdownPositionUpdates();
        }
      });
    },

    // Обработчик @input — используется только в режиме autoSearch
    handleSearchInput() {
      if (!this.autoSearch) return;

      clearTimeout(this.debounceTimer);

      const query = this.searchQuery.trim();

      if (!query) {
        this.searchPerformed = false;
        this.lastExternalSearchQuery = null;
        this.filteredOptions = [...this.options];
        if (this.externalSearch) {
          this.$emit("clear-search");
        }
        return;
      }

      if (
        !shouldDebounceBaseSelectSearch({
          externalSearch: this.externalSearch,
          autoSearch: this.autoSearch,
        })
      ) {
        this.handleSearch();
        return;
      }

      this.debounceTimer = setTimeout(() => {
        this.handleSearch();
      }, this.autoSearchDelay);
    },

    // Ввод нового текста отменяет текущее значение: label и поисковый запрос
    // используют один input, поэтому их нельзя считать одним состоянием.
    handleInlineSearchInput(event) {
      if (!this.inlineSearch) return;

      const currentQuery = getBaseSelectSearchQuery(
        event,
        this.searchQuery
      );
      if (currentQuery !== this.searchQuery) {
        this.searchQuery = currentQuery;
      }

      if (
        this.selectedOption &&
        this.searchQuery !== this.selectedOption.label
      ) {
        this.$emit("update:modelValue", null);
        this.$emit("change", null);
      }

      if (!this.externalSearch) {
        this.handleSearch();
        return;
      }

      this.handleSearchInput();
    },

    // Первый клик по выбранному inline input начинает новый поиск, а не требует
    // отдельного ручного удаления текущего label.
    handleInlineSearchMouseDown() {
      if (!this.inlineSearch || !this.selectedOption) return;

      this.searchQuery = "";
      this.searchPerformed = false;
      this.filteredOptions = [...this.options];
    },

    // Обрабатываем навигацию на input, чтобы document keydown не получил событие
    // второй раз после @keydown.stop в шаблоне.
    handleInlineSearchKeydown(event) {
      if (!this.inlineSearch) return;

      // Enter подтверждает символ для IME, а не запускает поиск.
      if (event.isComposing) return;

      switch (event.key) {
        case "ArrowDown":
          event.preventDefault();
          this.openDropdown();
          this.navigateOptions(1);
          break;
        case "ArrowUp":
          event.preventDefault();
          this.openDropdown();
          this.navigateOptions(-1);
          break;
        case "Enter":
          event.preventDefault();
          this.searchQuery = getBaseSelectSearchQuery(
            event,
            this.searchQuery
          );
          if (this.searchQuery.trim()) {
            this.forceSearch();
            break;
          }
          if (
            this.highlightedOption &&
            !this.highlightedOption.disabled
          ) {
            this.selectOption(this.highlightedOption);
          }
          break;
        case "Escape":
          event.preventDefault();
          this.closeDropdown();
          break;
        case "Tab":
          this.closeDropdown();
          break;
      }
    },

    // Inline-search живет в trigger, поэтому колесо над полем поиска иначе
    // прокручивает страницу, а не список options.
    handleInlineSearchWheel(event) {
      if (!this.inlineSearch || !this.isOpen) return;

      if (this.scrollDropdownContent(event.deltaY)) {
        event.preventDefault();
      }
    },

    // Запоминаем стартовую точку жеста над input, чтобы на мобильных
    // протащить touch-scroll в телепортированный список.
    handleInlineSearchTouchStart(event) {
      if (!this.inlineSearch || !this.isOpen) return;

      this.inlineSearchTouchY = event.touches[0]?.clientY || null;
    },

    // Когда жест начался на поле поиска, браузер по умолчанию скроллит страницу.
    // Для открытого inline-select это выглядит как сломанная прокрутка списка.
    handleInlineSearchTouchMove(event) {
      if (
        !this.inlineSearch ||
        !this.isOpen ||
        this.inlineSearchTouchY === null
      ) {
        return;
      }

      const currentY = event.touches[0]?.clientY;
      if (currentY === undefined) return;

      const deltaY = this.inlineSearchTouchY - currentY;
      this.inlineSearchTouchY = currentY;

      if (this.scrollDropdownContent(deltaY)) {
        event.preventDefault();
      }
    },

    // Единая точка ручной прокрутки dropdown-а. Возвращает true только когда
    // список реально сдвинулся, чтобы не блокировать нативный scroll страницы
    // на границах списка.
    scrollDropdownContent(deltaY) {
      const content = this.$refs.dropdownContent;
      if (!content || content.scrollHeight <= content.clientHeight) {
        return false;
      }

      const previousScrollTop = content.scrollTop;
      content.scrollTop += deltaY;

      if (content.scrollTop !== previousScrollTop) {
        this.handleDropdownScroll({ target: content });
        return true;
      }

      return false;
    },

    // Основной метод поиска — вызывается и вручную (кнопка/Enter), и из debounce
    handleSearch({ force = false } = {}) {
      const query = this.searchQuery.trim();

      if (!query) {
        this.filteredOptions = [...this.options];
        this.searchPerformed = false;
        this.lastExternalSearchQuery = null;
        if (this.externalSearch) {
          this.$emit("clear-search");
        }
        return;
      }

      this.searchPerformed = true;

      if (this.externalSearch) {
        if (
          !shouldEmitBaseSelectSearch({
            query,
            lastQuery: this.lastExternalSearchQuery,
            force,
          })
        ) {
          return;
        }

        this.lastExternalSearchQuery = query;
        // Делегируем фильтрацию серверу, filteredOptions обновится через watcher на options
        this.$emit("search", { query });
        return;
      }

      const lowerQuery = query.toLowerCase();
      this.filteredOptions = this.options.filter((option) =>
        option.label.toLowerCase().includes(lowerQuery)
      );

      // Устанавливаем первую опцию как выделенную
      this.highlightedOption = this.filteredOptions[0] || null;

      this.$emit("search", {
        query,
        results: this.filteredOptions.length,
      });
    },

    // Enter — явное действие пользователя: не ждем оставшийся debounce и не
    // допускаем второй такой же запрос от его таймера.
    forceSearch() {
      clearTimeout(this.debounceTimer);
      this.handleSearch({ force: true });
    },

    // Custom dropdown получает setter через slot, чтобы использовать тот же
    // searchQuery без знания о внутреннем состоянии BaseSelect.
    setCustomSearchQuery(value) {
      this.searchQuery = String(value || "");
    },

    closeDropdown() {
      this.isOpen = false;
    },

    // Обработка клавиш в поле поиска
    handleSearchKeydown(event) {
      if (event.key === "Escape") {
        this.clearSearch();
      }
    },

    // Очистка поиска
    clearSearch() {
      clearTimeout(this.debounceTimer);
      this.searchQuery = "";
      this.searchPerformed = false;
      this.lastExternalSearchQuery = null;
      this.filteredOptions = [...this.options];
      if (this.externalSearch) {
        this.$emit("clear-search");
      }
      if (this.$refs.searchInput) {
        this.$refs.searchInput.focus();
      }
    },

    // Скролл внутри дропдауна — для lazy load
    handleDropdownScroll(event) {
      const { scrollTop, scrollHeight, clientHeight } = event.target;
      if (scrollHeight - scrollTop <= clientHeight + 50) {
        this.$emit("dropdown-scroll-end");
      }
    },

    // Выбор опции
    selectOption(option) {
      if (this.disabled || option.disabled) return;

      this.$emit("update:modelValue", option.value);
      this.$emit("change", option);
      this.isOpen = false;
      this.searchQuery = this.inlineSearch ? option.label : "";
      this.searchPerformed = false;
    },

    // Обрабатываем mousedown при выборе мышью, иначе click по option может не
    // сработать и выбранное значение не применится к селектору.
    handleOptionMouseDown(event) {
      if (event.button === 0) {
        event.preventDefault();
      }
    },

    // Актуализирует данные формы после обновления выбранной записи в модалке
    // Без этого форма не обновит связанные поля, потому что id записи не меняется
    reselectSelectedOption() {
      if (!this.selectedOption) return;

      this.selectOption(this.selectedOption);
    },

    // Проверка выбранной опции
    isOptionSelected(option) {
      return String(option.value) === String(this.modelValue);
    },

    // Проверка выделенной опции
    isOptionHighlighted(option) {
      return (
        this.highlightedOption &&
        this.highlightedOption.value === option.value
      );
    },

    // Установка выделенной опции
    setHighlightedOption(option) {
      this.highlightedOption = option;
    },

    // Подсветка текста в результатах поиска
    highlightSearchText(text) {
      if (!this.searchQuery.trim() || !this.searchPerformed)
        return text;

      const regex = new RegExp(
        `(${this.escapeRegex(this.searchQuery.trim())})`,
        "gi"
      );
      return text.replace(regex, "<mark>$1</mark>");
    },

    // Экранирование специальных символов для regex
    escapeRegex(string) {
      return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    },

    // Обработка клика по кнопке
    handleButtonClick() {
      this.$emit("button-click");
      this.isOpen = false;
    },

    // BaseSelect не открывает модалки сам: wrapper получает id и решает,
    // какую доменную запись редактировать.
    editSelectedOption() {
      if (!this.modelValue) return;

      this.$emit("edit-option", this.modelValue);
      this.isOpen = false;
    },

    // Очистка выбора
    clearSelection() {
      if (this.disabled) return;

      this.$emit("update:modelValue", null);
      this.$emit("change", null);
      if (this.inlineSearch) {
        this.searchQuery = "";
        this.filteredOptions = [...this.options];
      }
    },

    // В закрытом inline-select input показывает label выбранной опции. Пока
    // список открыт без выбора, не стираем введённый пользователем запрос.
    syncInlineSearchQuery() {
      if (!this.inlineSearch) return;

      if (this.selectedOption) {
        this.searchQuery = this.selectedOption.label || "";
        return;
      }

      if (!this.isOpen) {
        this.searchQuery = "";
      }
    },

    // Навигация с клавиатуры
    handleKeydown(event) {
      if (!this.isOpen || this.customDropdown) return;

      switch (event.key) {
        case "ArrowDown":
          event.preventDefault();
          this.navigateOptions(1);
          break;
        case "ArrowUp":
          event.preventDefault();
          this.navigateOptions(-1);
          break;
        case "Enter":
          event.preventDefault();
          if (
            this.highlightedOption &&
            !this.highlightedOption.disabled
          ) {
            this.selectOption(this.highlightedOption);
          }
          break;
        case "Escape":
          event.preventDefault();
          this.isOpen = false;
          break;
        case "Tab":
          this.isOpen = false;
          break;
      }
    },

    // Навигация по опциям с клавиатуры
    navigateOptions(direction) {
      if (this.displayedOptions.length === 0) return;

      const currentIndex = this.displayedOptions.findIndex(
        (option) =>
          String(option.value) === String(this.highlightedOption?.value)
      );

      let newIndex;

      if (currentIndex === -1) {
        newIndex = direction > 0 ? 0 : this.displayedOptions.length - 1;
      } else {
        newIndex = currentIndex + direction;
      }

      if (newIndex < 0) newIndex = this.displayedOptions.length - 1;
      if (newIndex >= this.displayedOptions.length) newIndex = 0;

      this.highlightedOption = this.displayedOptions[newIndex];

      // Прокрутка к выделенному элементу
      this.$nextTick(() => {
        const highlightedElement = this.$refs.dropdown?.querySelector(
          ".select-option.is-highlighted"
        );
        if (highlightedElement && this.$refs.dropdownContent) {
          highlightedElement.scrollIntoView({ block: "nearest" });
        }
      });
    },

    // Focus может перейти из trigger в dropdown после blur. Откладываем
    // проверку до завершения браузерного focus-перехода.
    onBlur() {
      setTimeout(() => {
        if (
          !this.$el.contains(document.activeElement) &&
          !this.$refs.dropdown?.contains(document.activeElement)
        ) {
          this.isOpen = false;
        }
      }, 10);
    },
  },

  mounted() {
    // Инициализируем отфильтрованные опции
    this.filteredOptions = [...this.options];
  },

  beforeUnmount() {
    clearTimeout(this.debounceTimer);
    clearTimeout(this.inlineSearchLoadingHideTimer);
    this.clearDropdownPositionTimers();
    document.removeEventListener("keydown", this.handleKeydown);
    document.removeEventListener("click", this.handleClickOutside);
    window.removeEventListener(
      "scroll",
      this.updateDropdownPosition,
      true
    );
    window.removeEventListener("resize", this.updateDropdownPosition);
    this.removeDropdownViewportListeners();
    this.removeDropdownLayoutResizeObserver();
  },
};
</script>

<style scoped>
.custom-select {
  position: relative;
  width: 100%;
}

.select-trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 32px;
  min-height: 32px;
  padding: 0 10px;
  border: 1px solid #999;
  border-radius: 6px;
  background: #fff;
  cursor: pointer;
  transition: all 0.2s ease;
  outline: none;
  width: 100%;
  box-sizing: border-box;
  font-size: 14px;
}

.select-trigger:hover {
  border-color: var(--color-green);
}

.custom-select.is-disabled .select-trigger {
  cursor: not-allowed;
  opacity: 0.65;
}

.custom-select.is-disabled .select-trigger:hover {
  border-color: #ddd;
}

.select-trigger:focus {
  border-color: var(--color-green);
  box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
}

.select-trigger.invalid {
  border-color: var(--color-danger);
}

.select-trigger.invalid.is-open {
  box-shadow: none;
  border-color: var(--color-danger);
}

.selected-value {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
  overflow: hidden;
  min-width: 0;
  width: 0;
}

.custom-select.has-confirmed-selection .select-trigger {
  border-color: rgba(0, 191, 165, 0.7);
  background: #f8fffd;
}

.custom-select.has-custom-dropdown .selected-value {
  min-height: 28px;
  overflow: visible;
}

.custom-select.has-custom-dropdown .selected-value__text {
  overflow: visible;
  text-overflow: clip;
}

.custom-select.has-custom-dropdown .emoji-selected-value {
  min-height: 28px;
  overflow: visible;
}

.custom-select.has-custom-dropdown .emoji-selected-value__icon {
  overflow: visible;
  line-height: 1.5;
}

.select-color-dot {
  flex: 0 0 auto;
  width: 8px;
  height: 8px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 50%;
}

/* Dropdown позиционируется относительно .custom-select. */
.select-dropdown {
  max-width: calc(100dvw - 24px);
  background: white;
  border: 1px solid #999;
  border-radius: 6px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  overscroll-behavior: contain;
}

.select-dropdown--custom {
  max-height: min(400px, calc(100dvh - 24px));
  overflow-y: auto;
}

/* Стили поиска */
.search-container {
  padding: 12px;
  border-bottom: 1px solid #f1f5f9;
  background: #f8fafc;
  display: flex;
  gap: 8px;
  align-items: center;
}

.search-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  flex: 1;
}

.search-icon {
  position: absolute;
  left: 10px;
  color: #999;
}

.search-input {
  width: 100%;
  height: 32px;
  min-height: 32px;
  padding: 0 30px 0 32px;
  border: 1px solid #999;
  border-radius: 6px;
  font-size: 14px;
  line-height: 30px;
  outline: none;
  box-sizing: border-box;
  transition: border-color 0.2s ease;
}

.search-input:focus {
  border-color: var(--color-green);
  box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.1);
}

.clear-search {
  position: absolute;
  right: 8px;
  background: transparent;
  border: none;
  color: #999;
  cursor: pointer;
  padding: 4px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.clear-search:hover {
  background: #f3f4f6;
  color: #374151;
}

.search-button {
  height: 32px;
  min-height: 32px;
  padding: 0 12px;
  background: var(--color-green);
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.search-button:hover:not(:disabled) {
  background: var(--color-green-hover);
}

.search-button:disabled {
  background: #cbd5e1;
  color: #64748b;
  cursor: not-allowed;
}

.dropdown-content {
  max-height: min(
    256px,
    var(--base-select-dropdown-content-max-height, 256px)
  );
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
}

.select-option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 32px;
  padding: 5px 10px;
  cursor: pointer;
  transition: all 0.2s ease;
  border-bottom: 1px solid #f7fafc;
  box-sizing: border-box;
}

.select-option:last-child {
  border-bottom: none;
}

.select-option:hover {
  background: #d5ecea;
}

.select-option.is-selected {
  background: var(--color-green);
  color: #fff;
}

.select-option.is-selected.is-highlighted {
  background: var(--color-green);
  color: #fff;
}

.select-option.is-highlighted {
  background: #d0f5e9;
}

.select-option.is-disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.option-label {
  display: flex;
  align-items: center;
  flex: 1 1 auto;
  gap: 6px;
  min-width: 0;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.2;
}

.option-label > span:last-child {
  min-width: 0;
  overflow-wrap: anywhere;
}

.option-label mark {
  background: #ffeb3b;
  color: #000;
  padding: 1px 2px;
  border-radius: 2px;
}

.checkmark {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  margin-left: 8px;
  flex: 0 0 auto;
}

.no-results {
  padding: 12px 10px;
  text-align: center;
  color: #6b7280;
  font-size: 14px;
}

.search-info {
  padding: 6px 10px;
  background: #f8fafc;
  border-top: 1px solid #e5e7eb;
  font-size: 12px;
  color: #6b7280;
  text-align: center;
}

.select-button {
  display: flex;
  align-items: center;
  gap: 6px;
  min-height: 32px;
  padding: 0 10px;
  background: #f8fafc;
  border-top: 1px solid #999;
  cursor: pointer;
  transition: all 0.2s ease;
  box-sizing: border-box;
}

.select-button:hover {
  background: var(--color-green-hover);
  color: white;
}

.button-icon {
  font-size: 16px;
  font-weight: bold;
}

.button-label {
  font-size: 14px;
  font-weight: 500;
}

/* Скрытие нативного скроллбара для красоты */
.dropdown-content::-webkit-scrollbar {
  width: 6px;
}

.dropdown-content::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.dropdown-content::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.dropdown-content::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}
</style>
